import React, { useMemo, useState } from 'react'
import { useSourceView } from './UseSourceView.ts'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'

type UpSetParams = {
  title?: string
  maxBuckets?: number
  topK?: number
  minMagnitude?: number
}

const EMPTY_PATH: string[] = []

type Intersection = {
  mask: number
  members: boolean[]
  taxaCount: number
  magnitudeSum: number
}

function shortLabel(s: string, max = 18) {
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

function dotClass(on: boolean, selected: boolean) {
  if (on) return 'bg-black/70 border-black/30'
  return selected ? 'bg-black/5 border-black/25' : 'bg-transparent border-black/15'
}

function fmtNumber(x: number) {
  if (!Number.isFinite(x)) return String(x)
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`
  if (x === Math.floor(x)) return String(x)
  return x.toFixed(2)
}

function maskTooltip(mask: number, labels: string[]) {
  const present: string[] = []
  const absent: string[] = []

  labels.forEach((label, i) => {
    if (mask & (1 << i)) present.push(label)
    else absent.push(label)
  })

  const symbolic = present.map((l) => l).join(' ∩ ') + (absent.length ? ' ∩ ' + absent.map((l) => `¬${l}`).join(' ∩ ') : '')

  return {
    symbolic,
    present,
    absent
  }
}

export default function UpSetSink({ sourceId, config }: { sourceId: SourceId; sinkId: SinkId; config: SinkConfig<UpSetParams> }) {
  const { snapshot, shared, status } = useSourceView(sourceId)

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0

  const bucketsRes = useTransformer(sourceId, undefined, 'kronaFilteredBuckets', {
    selectedPath,
    gradeThreshold
  })

  const maxBuckets = config.params?.maxBuckets ?? 8
  const topK = config.params?.topK ?? 12
  const minMagnitude = config.params?.minMagnitude ?? 0

  // Toggle: how to size bars (and how to sort intersections)
  const [metric, setMetric] = useState<'taxaCount' | 'magnitudeSum'>('taxaCount')
  const [selectedMask, setSelectedMask] = useState<number | null>(null)

  const { labels, setSizes, intersectionsAll } = useMemo(() => {
    if (bucketsRes.status !== 'ready') {
      return {
        labels: [] as string[],
        setSizes: { taxaCount: [] as number[], magnitudeSum: [] as number[] },
        intersectionsAll: [] as Intersection[]
      }
    }

    const depth = selectedPath.length
    const buckets = bucketsRes.value.slice(0, maxBuckets)
    const n = buckets.length

    // For each bucket:
    // - taxa presence set (at this depth)
    // - magnitude sum (at this depth)
    const bucketTaxaSets: Array<Set<string>> = Array.from({ length: n }, () => new Set<string>())
    const bucketMagSums: number[] = Array(n).fill(0)

    // For each taxon: membership mask + total magnitude across buckets
    const taxonMask = new Map<string, number>()
    const taxonMag = new Map<string, number>()

    buckets.forEach((b, idx) => {
      const bit = 1 << idx
      for (const v of b.values) {
        const tax = v.taxa[depth] ?? '(n/a)'
        const mag = v.magnitude ?? 0

        // IMPORTANT: allow 0 when minMagnitude = 0
        if (mag < minMagnitude) continue

        bucketTaxaSets[idx]!.add(tax)
        bucketMagSums[idx] = (bucketMagSums[idx] ?? 0) + mag

        taxonMask.set(tax, (taxonMask.get(tax) ?? 0) | bit)
        taxonMag.set(tax, (taxonMag.get(tax) ?? 0) + mag)
      }
    })

    const setTaxaCount = bucketTaxaSets.map((s) => s.size)
    const setMagnitudeSum = bucketMagSums

    // Intersection aggregates by mask:
    const agg = new Map<number, { taxaCount: number; magnitudeSum: number }>()
    for (const [tax, m] of taxonMask.entries()) {
      const prev = agg.get(m) ?? { taxaCount: 0, magnitudeSum: 0 }
      prev.taxaCount += 1
      prev.magnitudeSum += taxonMag.get(tax) ?? 0
      agg.set(m, prev)
    }

    const intersectionsAll: Intersection[] = Array.from(agg.entries())
      .filter(([mask]) => mask !== 0)
      .map(([mask, v]) => ({
        mask,
        taxaCount: v.taxaCount,
        magnitudeSum: v.magnitudeSum,
        members: Array.from({ length: n }, (_, i) => (mask & (1 << i)) !== 0)
      }))

    const labels = buckets.map((b) => b.bucketLabel)
    return {
      labels,
      setSizes: { taxaCount: setTaxaCount, magnitudeSum: setMagnitudeSum },
      intersectionsAll
    }
  }, [bucketsRes, selectedPath.length, maxBuckets, minMagnitude])

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (bucketsRes.status === 'idle' || bucketsRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (labels.length === 0) return <div className="text-sm opacity-70">No buckets in view.</div>
  if (intersectionsAll.length === 0) return <div className="text-sm opacity-70">No intersections at this level.</div>

  const cols = [...intersectionsAll]
    .sort((a, b) => (metric === 'taxaCount' ? b.taxaCount - a.taxaCount : b.magnitudeSum - a.magnitudeSum))
    .slice(0, topK)

  const nBuckets = labels.length
  const nCols = cols.length

  const maxIntersectionVal = Math.max(...cols.map((c) => (metric === 'taxaCount' ? c.taxaCount : c.magnitudeSum)))
  const maxSetVal = Math.max(...setSizes[metric])

  // Layout knobs (reduced whitespace)
  const topBarsH = 140
  const matrixHeaderH = 30 // SMALL header (no more huge gap)
  const rowH = 36
  const leftW = 320
  const gap = 10

  const maskToLabel = (mask: number) => {
    const names = labels.filter((_, i) => (mask & (1 << i)) !== 0)
    return names.length ? names.join(' ∩ ') : '(none)'
  }

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="text-xs opacity-60 flex items-center gap-3 flex-wrap pl-2">
        <span>
          Showing first <span className="font-mono">{labels.length}</span> buckets and top <span className="font-mono">{cols.length}</span>{' '}
          intersections.
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-black/70" /> included
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full border border-black/20 bg-white" /> not included
        </span>
      </div>

      {/* Toggle */}
      <div className="flex items-center gap-2 text-xs pl-2">
        <span className="opacity-60">Size metric:</span>
        <button className={`btn btn-xs ${metric === 'taxaCount' ? '' : 'btn-ghost'}`} onClick={() => setMetric('taxaCount')}>
          Taxa count
        </button>
        <button className={`btn btn-xs ${metric === 'magnitudeSum' ? '' : 'btn-ghost'}`} onClick={() => setMetric('magnitudeSum')}>
          Magnitude sum
        </button>

        {selectedMask != null && (
          <span className="ml-2 opacity-70">
            Selected: <span className="font-mono">{maskToLabel(selectedMask)}</span>
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto rounded border border-black/10 bg-base-100 p-3">
        {/* 2x2 canonical layout */}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `${leftW}px 1fr`,
            gridTemplateRows: `${topBarsH}px 1fr`,
            gap
          }}
        >
          {/* top-left spacer */}
          <div />

          {/* top-right: intersection size bars (one per column) */}
          <div className="overflow-hidden">
            <div
              className="grid"
              style={{
                gridTemplateColumns: `repeat(${nCols}, minmax(24px, 1fr))`,
                gap
              }}
            >
              {cols.map((c) => {
                const val = metric === 'taxaCount' ? c.taxaCount : c.magnitudeSum
                const h = maxIntersectionVal > 0 ? Math.max(2, (val / maxIntersectionVal) * (topBarsH - 28)) : 2
                const selected = selectedMask === c.mask

                const { symbolic, present, absent } = maskTooltip(c.mask, labels)

                return (
                  <button
                    key={`ibar-${c.mask}`}
                    className={`relative flex flex-col items-center justify-end rounded px-1 pb-1 hover:bg-base-200/60 ${
                      selected ? 'bg-base-200/80' : ''
                    }`}
                    style={{ height: topBarsH }}
                    title={
                      `Intersection: ${symbolic}\n\n` + `Taxa present in:\n${present.join(', ')}\n\n` + `Absent from:\n${absent.join(', ')}`
                    }
                    onClick={() => setSelectedMask((prev) => (prev === c.mask ? null : c.mask))}
                  >
                    <div className="text-[10px] font-mono opacity-70 mb-1">{fmtNumber(val)}</div>
                    <div className="w-full rounded bg-black/60" style={{ height: h }} />
                  </button>
                )
              })}
            </div>
          </div>

          {/* bottom-left: set sizes (no huge header spacer anymore) */}
          <div className="overflow-hidden">
            <div className="text-xs opacity-70 mb-2">Set size ({metric === 'taxaCount' ? 'taxa count' : 'magnitude sum'})</div>

            <div className="flex flex-col" style={{ gap }}>
              {labels.map((l, i) => {
                const val = setSizes[metric][i] ?? 0
                const wPct = maxSetVal > 0 ? (val / maxSetVal) * 100 : 0
                return (
                  <div key={`set-${i}`} className="flex items-center gap-2" style={{ height: rowH }}>
                    <div className="w-40 text-xs opacity-70 truncate" title={l}>
                      {shortLabel(l, 22)}
                    </div>
                    <div className="flex-1 h-3.5 bg-black/10 rounded overflow-hidden">
                      <div className="h-3.5 bg-black/60" style={{ width: `${wPct}%` }} />
                    </div>
                    <div className="w-14 text-xs font-mono opacity-70 text-right">{fmtNumber(val)}</div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* bottom-right: matrix + connector lines */}
          <div className="overflow-hidden">
            {/* small header row */}
            <div
              style={{
                height: matrixHeaderH,
                display: 'grid',
                gridTemplateColumns: `repeat(${nCols}, minmax(24px, 1fr))`,
                gap
              }}
              className="items-end"
            >
              {cols.map((c) => {
                const selected = selectedMask === c.mask
                return (
                  <div key={`h-${c.mask}`} className={`rounded px-1 py-1 ${selected ? 'bg-base-200/80' : ''}`} title={maskToLabel(c.mask)}>
                    <div className="text-[10px] font-mono opacity-60 text-center">
                      {c.members.filter(Boolean).length}/{nBuckets}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* matrix area */}
            <div className="relative mt-1">
              <div className="flex flex-col" style={{ gap }}>
                {labels.map((bucketLabel, rowIdx) => (
                  <div
                    key={`mrow-${rowIdx}`}
                    className="grid relative"
                    style={{
                      gridTemplateColumns: `repeat(${nCols}, minmax(24px, 1fr))`,
                      gap,
                      height: rowH
                    }}
                  >
                    {cols.map((c) => {
                      const on = c.members[rowIdx] ?? false
                      const selected = selectedMask === c.mask
                      const { symbolic, present, absent } = maskTooltip(c.mask, labels)
                      return (
                        <button
                          key={`dot-${rowIdx}-${c.mask}`}
                          className={`flex items-center justify-center rounded hover:bg-base-200/50 ${selected ? 'bg-base-200/40' : ''}`}
                          title={
                            `Intersection: ${symbolic}\n\n` +
                            `Taxa present in:\n${present.join(', ')}\n\n` +
                            `Absent from:\n${absent.join(', ')}`
                          }
                        >
                          <div
                            className={`rounded-full border ${dotClass(on, selected)}`}
                            style={{
                              width: 'clamp(16px, 2.6vw, 30px)',
                              height: 'clamp(16px, 2.6vw, 30px)'
                            }}
                          />
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
